import { useAnalyticsConsent } from '../analytics/useAnalyticsConsent';
import { item, track } from '../analytics/analytics';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useParams, useNavigate } from 'react-router-dom';
import type { ProductQuery, ProductSort } from '@skytech/shared';

import { useCatalogFacets, useCategories, useProducts } from '../products/api';
import { ProductCard } from '../../components/ui/ProductCard';
import { ProductCardSkeleton } from '../../components/ui/Skeleton';
import { formatKES } from '../../lib/format';
import { Modal } from '../../components/ui/Modal';
import { FilterPanel, type FilterState } from './FilterPanel';

const SORTS: Array<{ value: ProductSort; label: string }> = [
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'name_asc', label: 'Name: A–Z' },
  { value: 'name_desc', label: 'Name: Z–A' },
  { value: 'rating_desc', label: 'Best rated' },
];

const EMPTY_FILTERS: FilterState = {
  category: undefined,
  brand: [],
  minPrice: undefined,
  maxPrice: undefined,
  inStock: false,
  minRating: undefined,
};

type CategoryLite = { slug: string; name: string; children?: CategoryLite[] };

/**
 * Catalog / search results (SRS §4.3). Filters, sort and search term all live in the
 * URL query string, so a filtered view is shareable and survives back/forward. The
 * grid is 2/3/4 columns across breakpoints and pages via a cursor-based "Load more".
 */
export function CatalogPage(): JSX.Element {
  const consentVersion = useAnalyticsConsent();
  const navigate = useNavigate();
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [params, setParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const { data: categories = [] } = useCategories();

  const q = params.get('q') ?? undefined;
  const sort = SORTS.find((s) => s.value === params.get('sort'))?.value ?? 'newest';

  const filters: FilterState = useMemo(
    () => ({
      category: params.get('category') ?? categorySlug,
      brand: params.getAll('brand'),
      minPrice: params.get('minPrice') ? Number(params.get('minPrice')) : undefined,
      maxPrice: params.get('maxPrice') ? Number(params.get('maxPrice')) : undefined,
      inStock: params.get('inStock') === 'true',
      minRating: params.get('minRating') ? Number(params.get('minRating')) : undefined,
    }),
    [params, categorySlug],
  );

  const { data: facets } = useCatalogFacets(filters.category);

  const query: ProductQuery = { ...filters, q, sort, limit: 20 };
  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchNextPageError,
  } = useProducts(query);

  const products = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const total = data?.pages[0]?.meta?.total ?? 0;
  useEffect(() => {
    if (products.length)
      track(
        'view_item_list',
        { item_list_id: filters.category ?? 'catalog', items: products.map((p) => item(p)) },
        'list:' + (filters.category ?? '') + ':' + products.map((p) => p.id).join(','),
      );
  }, [products, filters.category, consentVersion]);
  const activeChips = describeActiveFilters(filters, categories, facets);

  /** Writes the next filter/sort/search state into the URL. */
  function patch(next: Partial<{ filters: FilterState; sort: ProductSort; q: string | null }>) {
    const p = new URLSearchParams(params);
    if (next.q !== undefined) {
      if (next.q) p.set('q', next.q);
      else p.delete('q');
    }
    if (next.sort) p.set('sort', next.sort);
    if (next.filters) {
      const f = next.filters;
      p.delete('brand');
      f.brand.forEach((b) => p.append('brand', b));
      setOrDelete(p, 'category', f.category);
      setOrDelete(p, 'minPrice', f.minPrice);
      setOrDelete(p, 'maxPrice', f.maxPrice);
      setOrDelete(p, 'minRating', f.minRating);
      if (f.inStock) p.set('inStock', 'true');
      else p.delete('inStock');
    }
    if (next.filters && next.filters.category !== filters.category) {
      p.delete('category');
      const target = next.filters.category
        ? `/category/${encodeURIComponent(next.filters.category)}`
        : '/products';
      navigate(`${target}${p.size ? `?${p.toString()}` : ''}`);
    } else {
      // Keep the category in the path, with facets encoded only once.
      if (categorySlug) p.delete('category');
      setParams(p, { replace: true });
    }
  }

  const clearAll = () => patch({ filters: EMPTY_FILTERS });

  const heading = q
    ? `Results for “${q}”`
    : filters.category
      ? categoryName(categories, filters.category)
      : 'All products';

  return (
    <div
      data-page-ready={!isLoading && !isError}
      className="mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-8"
    >
      {/* §4.3: keep the collection title, navigation and controls compact above results. */}
      <header aria-label="Collection overview">
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2 border-b border-frost pb-5">
          <h1 className="min-w-0 break-words text-[1.75rem] font-light leading-tight tracking-tight text-navy-900 sm:text-3xl">
            {heading}
          </h1>
          <p className="shrink-0 text-xs tabular-nums text-neutral sm:text-sm" role="status">
            {isLoading
              ? 'Loading products…'
              : `${total} ${total === 1 ? 'product' : 'products'}${isFetching ? ' · Updating…' : ''}`}
          </p>
        </div>
        <div className="mt-4 flex min-w-0 items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border border-frost bg-white px-4 text-sm font-medium text-navy-900 shadow-sm transition-colors hover:border-sky-500 lg:hidden"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden
            >
              <path d="M4 6h16M7 12h10M10 18h4" strokeLinecap="round" />
            </svg>
            Filters
            {activeChips.length > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-sky-600 px-1 text-xs font-medium text-white">
                {activeChips.length}
              </span>
            )}
          </button>
          <span className="hidden text-sm font-medium text-navy-900 lg:block">Refine results</span>
          <label className="flex min-w-0 items-center gap-2 text-xs text-neutral">
            <span className="hidden shrink-0 sm:block">Sort by</span>
            <span className="sr-only sm:hidden">Sort by</span>
            <select
              id="sort"
              value={sort}
              onChange={(e) => patch({ sort: e.target.value as ProductSort })}
              className="min-h-11 w-full min-w-0 max-w-48 rounded-full border border-frost bg-white px-4 py-2 text-sm text-navy-900 shadow-sm focus-visible:outline-2 focus-visible:outline-sky-500 sm:w-48"
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>
      <div className="mt-5 flex gap-6">
        {/* Sidebar (desktop) */}
        <aside className="hidden w-64 shrink-0 lg:block" aria-labelledby="desktop-filters">
          <h2 id="desktop-filters" className="sr-only">
            Filter products
          </h2>
          <div className="catalog-filter-scroll sticky top-32 max-h-[calc(100dvh-9rem)] overflow-y-auto rounded-xl border border-frost bg-white p-4 shadow-sm">
            <FilterPanel
              categories={categories}
              facets={facets}
              value={filters}
              onChange={(f) => patch({ filters: f })}
            />
          </div>
        </aside>

        {/* Grid */}
        <section className="min-w-0 flex-1" aria-labelledby="catalog-results">
          <h2 id="catalog-results" className="sr-only">
            Product results
          </h2>
          {activeChips.length > 0 && (
            <div className="mb-5 flex flex-wrap items-center gap-2">
              {activeChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => patch({ filters: chip.remove(filters) })}
                  className="group inline-flex items-center gap-1.5 rounded-full border border-sky-500/40 bg-sky-500/[0.06] py-1 pl-3 pr-2 text-xs font-medium text-sky-ink hover:border-sky-500"
                >
                  {chip.label}
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden
                    className="text-neutral group-hover:text-sky-600"
                  >
                    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                  </svg>
                </button>
              ))}
              <button
                type="button"
                onClick={clearAll}
                className="ml-1 text-xs font-medium text-neutral underline-offset-2 hover:text-navy-900 hover:underline"
              >
                Clear all
              </button>
            </div>
          )}

          {isError && products.length === 0 ? (
            <div role="alert" className="rounded-lg border border-frost bg-white p-6">
              <h2 className="text-lg font-light text-navy-900">Products could not load</h2>
              <p className="mt-2 text-sm text-neutral">Please try again in a moment.</p>
              <button
                className="mt-4 min-h-11 rounded-md bg-sky-600 px-5 text-white"
                onClick={() => void refetch()}
              >
                Try again
              </button>
            </div>
          ) : isLoading ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="rounded-lg border border-frost bg-white p-12 text-center">
              <p className="text-navy-900">No products match these filters.</p>
              <p className="mt-1 text-sm text-neutral">
                Try clearing a filter or searching a different term.
              </p>
              {activeChips.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="mt-4 rounded-md border border-sky-500 px-5 py-2 text-sm font-medium text-sky-ink hover:bg-sky-500/5"
                >
                  Clear all filters
                </button>
              )}
              {q && (
                <button
                  type="button"
                  onClick={() => patch({ q: null })}
                  className="mt-4 min-h-11 rounded-md border border-sky-500 px-5 text-sm text-sky-ink"
                >
                  Clear search
                </button>
              )}
              <Link
                to="/products"
                className="mt-3 flex min-h-11 items-center justify-center text-sm text-sky-ink underline"
              >
                Browse all products →
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {products.map((p, i) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    listId={filters.category ?? 'catalog'}
                    priority={i === 0}
                    largeMobileImage
                  />
                ))}
              </div>
              <p className="mt-6 text-center text-sm text-neutral" role="status">
                Showing {products.length} of {total} products
                {!hasNextPage ? ' · You’ve seen them all' : ''}
              </p>
              {isFetchNextPageError && (
                <p role="alert" className="mt-3 text-center text-sm text-danger">
                  More products could not load. Your current results are still here; try again
                  below.
                </p>
              )}
              {hasNextPage && (
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={() => fetchNextPage()}
                    disabled={isFetching}
                    className="rounded-md border border-sky-500 px-8 py-3 text-sm font-medium text-sky-ink transition-colors hover:bg-sky-500/5 disabled:opacity-50"
                  >
                    {isFetching
                      ? 'Loading…'
                      : isFetchNextPageError
                        ? 'Try loading more'
                        : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Filter drawer (mobile) */}
      <Modal
        open={drawerOpen}
        onClose={closeDrawer}
        label="Filter products"
        presentation="drawer"
        className="flex h-full w-[22rem] max-w-[90%] flex-col bg-white shadow-hover"
        backdropClassName="bg-navy-900/40"
      >
        <div className="flex items-center justify-between border-b border-frost px-5 py-4">
          <h2 className="text-lg font-light text-navy-900">Filters</h2>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="tap-target text-navy-700"
            aria-label="Close filters"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <FilterPanel
            categories={categories}
            facets={facets}
            value={filters}
            onChange={(f) => patch({ filters: f })}
          />
        </div>
        <div className="flex items-center gap-3 border-t border-frost p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <button
            type="button"
            onClick={clearAll}
            className="min-h-11 text-sm font-medium text-neutral hover:text-navy-900"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="flex-1 rounded-md bg-sky-600 py-2.5 text-sm font-medium text-white"
          >
            Show {total} result{total === 1 ? '' : 's'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

interface Chip {
  key: string;
  label: string;
  remove: (f: FilterState) => FilterState;
}

/** Applied filters as removable chips (category, brands, price, availability, rating). */
function describeActiveFilters(
  filters: FilterState,
  categories: CategoryLite[],
  facets: { priceMin: number; priceMax: number } | undefined,
): Chip[] {
  const chips: Chip[] = [];

  if (filters.category) {
    chips.push({
      key: 'category',
      label: categoryName(categories, filters.category),
      remove: (f) => ({ ...f, category: undefined }),
    });
  }

  filters.brand.forEach((b) =>
    chips.push({
      key: `brand:${b}`,
      label: b,
      remove: (f) => ({ ...f, brand: f.brand.filter((x) => x !== b) }),
    }),
  );

  if (filters.minPrice !== undefined || filters.maxPrice !== undefined) {
    const lo = filters.minPrice ?? facets?.priceMin ?? 0;
    const hi = filters.maxPrice;
    const label =
      filters.minPrice !== undefined && filters.maxPrice !== undefined
        ? `${formatKES(String(lo))} – ${formatKES(String(hi))}`
        : filters.minPrice !== undefined
          ? `From ${formatKES(String(lo))}`
          : `Up to ${formatKES(String(hi))}`;
    chips.push({
      key: 'price',
      label,
      remove: (f) => ({ ...f, minPrice: undefined, maxPrice: undefined }),
    });
  }

  if (filters.inStock) {
    chips.push({ key: 'inStock', label: 'In stock', remove: (f) => ({ ...f, inStock: false }) });
  }

  if (filters.minRating !== undefined) {
    chips.push({
      key: 'rating',
      label: `${filters.minRating}★ & up`,
      remove: (f) => ({ ...f, minRating: undefined }),
    });
  }

  return chips;
}

function setOrDelete(p: URLSearchParams, key: string, val: string | number | undefined) {
  if (val === undefined || val === '') p.delete(key);
  else p.set(key, String(val));
}

function categoryName(cats: CategoryLite[], slug: string): string {
  return findCategoryName(cats, slug) ?? 'Products';
}

function findCategoryName(cats: CategoryLite[], slug: string): string | null {
  for (const c of cats) {
    if (c.slug === slug) return c.name;
    if (c.children) {
      const found = findCategoryName(c.children, slug);
      if (found) return found;
    }
  }
  return null;
}
